import clsx from 'clsx';
import { Body, Button, Heading } from 'copilot-design-system';
import _ from 'lodash';
import React from 'react';

import { PlaceholderImage } from 'src/components/icons/PlaceholderImage';
import { RichTextStyles } from 'src/components/tiptap/RichTextStyles';
import { TAILWIND_BREAKPOINT_WIDTHS } from 'src/constants/uiConsts';
import { useWindowSize } from 'src/hooks/useWindowResize';
import { StripeCurrencyFormatterAdapter } from 'src/legacy/components/Formatters/Formatters';
import { ProductStoreItemSkeleton } from 'src/routes/billing/store/components/Preview/ProductStoreItemSkeleton';
import { ActiveFormSectionType } from 'src/routes/billing/store/components/ProductStoreEditor';
import { ProductTitleHeader } from 'src/routes/billing/store/components/ProductTitleHeader';
import { BillingPeriodMapper } from 'src/services/api/productsApi';
import { RichProductStore } from 'src/services/api/productStoreApi';

interface Props {
  productStore: RichProductStore;
  onClickPurchase: (productId: string, priceId: string) => void;
  isMobilePreview?: boolean;
  isPreview?: boolean;
  activeFormSection?: ActiveFormSectionType | null;
}
const TAILWIND_BREAKPOINT_TABLET_WIDTH = 770;

const MOBILE_BREAKPOINT_WIDTH = 1100;
const THREE_COLUMN_GRID_WIDTH = 1200;

export enum LayoutType {
  Plans = 'plans',
  OneTime = 'oneTime',
}

const TD_INNER_STYLES =
  'align-top w-[100%] border-l border-r border-b-0 border-t-0 border-solid border-non-hover-border bg-white ml-2';

const getOneTimeTableWidth = (
  isProductStorePreview: boolean,
  productCount: number,
  isMobile: boolean,
  isMobilePreview: boolean,
  isTablet: boolean,
  isPreviewForSingleItem: boolean,
): string => {
  switch (true) {
    case isProductStorePreview && productCount === 1 && !isPreviewForSingleItem:
      return 'w-[51%]';
    case isPreviewForSingleItem && productCount === 1:
      return 'w-full';
    case isProductStorePreview ||
      isMobile ||
      isMobilePreview ||
      (isTablet && productCount >= 2) ||
      productCount >= 4:
      return 'w-full';
    case productCount > 2 && !isTablet:
      return 'w-9/12';
    case productCount > 1 || isTablet:
      return 'w-[51%]';
    default:
      return 'w-3/12';
  }
};

const getSubscriptionTableWidth = (
  subscriptionCount: number,
  isMobile: boolean,
  isMobilePreview: boolean,
  isPreviewForSingleItem: boolean,
): string => {
  switch (true) {
    case subscriptionCount >= 2 ||
      isMobile ||
      isMobilePreview ||
      (isPreviewForSingleItem && subscriptionCount === 1):
      return 'w-full';
    case subscriptionCount === 1:
      return 'w-[51%]';
    default:
      return 'w-4/12';
  }
};
const getDescription = (columns: Array<{ description?: string }>) => {
  return columns.some((col) =>
    Boolean(col.description?.replace(/<[^>]*>/g, '').trim()),
  );
};

const getMaxColumnsLength = (
  isMobile: boolean,
  isMobilePreview: boolean,
  isTablet: boolean,
  isPreview: boolean,
  isPreviewMobile: boolean,
  isDesktopPreview: boolean,
  isThreeColumnGrid: boolean,
): number => {
  switch (true) {
    // Case 1: Mobile devices, mobile preview, or mobile preview mode
    case isMobile || isMobilePreview || (isPreviewMobile && isPreview):
      return 1;

    // Case 3: Tablet, desktop preview, or desktop in preview mode
    case isTablet || isDesktopPreview:
      return 2;

    // Case 2: Desktop devices, not in preview mode
    case isThreeColumnGrid:
      return 3;

    // Default case: Fallback to 4 columns
    default:
      return 4;
  }
};

export const ProductStorePage: React.FC<Props> = ({
  productStore,
  onClickPurchase,
  isMobilePreview = false,
  isPreview = false,
  activeFormSection,
}) => {
  const { oneTimeProducts, subscriptions } = productStore;
  const validSubscriptions = isPreview
    ? subscriptions.filter((obj) => obj.isEditing || obj.isValid)
    : subscriptions;

  const validOneTimeProducts = isPreview
    ? oneTimeProducts.filter((obj) => obj.isEditing || obj.isValid)
    : oneTimeProducts;

  const isShowOnlySubscriptions =
    productStore.productStorePreferences.showOnlySubscriptions;
  const isShowOnlyOneTimeProducts =
    productStore.productStorePreferences.showOnlyOneTimeProducts;

  const { width } = useWindowSize(1);

  const isMobile = width < TAILWIND_BREAKPOINT_WIDTHS.sm;
  // On preview, we only support mobile or desktop.
  // Hence everything under desktop mode is treated as mobile
  const isPreviewMobile = width < MOBILE_BREAKPOINT_WIDTH;
  const isTablet = width < TAILWIND_BREAKPOINT_TABLET_WIDTH;
  const isDesktop = width < TAILWIND_BREAKPOINT_WIDTHS.xxl;
  // Need to take care of this particular breakpoint in store preview
  // because in preview, when we've 3 or 4 products, cards become too small
  // and we need to take full available width then.
  const isDesktopPreview = isPreview && isDesktop;
  const isPreviewForSingleItem = isPreview && isPreviewMobile;
  // For the purpose of the store, we consider a three-column
  // to be a grid that has a width of less than 1200px.
  // This is because we want to ensure that the store looks good on smaller screens
  const isThreeColumnGrid = width < THREE_COLUMN_GRID_WIDTH;

  const maxSubscriptionColumnsLength = getMaxColumnsLength(
    isMobile,
    isMobilePreview,
    isTablet,

    isPreview,
    isPreviewMobile,
    isDesktopPreview,
    isThreeColumnGrid,
  );

  const maxOneTimeColumnsLength = getMaxColumnsLength(
    isMobile,
    isMobilePreview,
    isTablet,
    isPreview,
    isPreviewMobile,
    isDesktopPreview,
    isThreeColumnGrid,
  );

  const subscriptionColumnsLength = _.min([
    validSubscriptions.length,
    maxSubscriptionColumnsLength,
  ]);
  const oneTimeColumnsLength = _.min([
    validOneTimeProducts.length,
    maxOneTimeColumnsLength,
  ]);
  const largeScreenSubscriptionTableWidth = getSubscriptionTableWidth(
    validSubscriptions.length,
    isMobile,
    isMobilePreview,
    isPreviewForSingleItem,
  );

  const largeScreenOneTimeTableWidth = getOneTimeTableWidth(
    isDesktopPreview,
    validOneTimeProducts.length,
    isMobile,
    isMobilePreview,
    isTablet,
    isPreviewForSingleItem,
  );

  const subscriptionColumns = _.chunk(
    validSubscriptions,
    subscriptionColumnsLength,
  );
  const oneTimeColumns = _.chunk(validOneTimeProducts, oneTimeColumnsLength);
  const initialSubscriptionProduct = subscriptionColumns.at(0)?.at(0)
    ?.product?.name;
  const initialOneTimeProduct = !!validOneTimeProducts.at(0)?.product?.name;

  const formatCurrency = (value: number) => {
    return StripeCurrencyFormatterAdapter({
      value: value,
      currency: productStore.productStorePreferences.currency,
    }).replace('.00', '');
  };

  const showPlaceholderImages = oneTimeProducts.reduce(
    (acc, column) => acc || Boolean(column.imageUrl),
    false,
  );

  // The approach use for laying out the store:
  // We will be using tables in order to layout our products. Each row will be laid on top of each other with no padding,
  // with the top data cells having top, left, and right padding, middle data cells only having left and right padding,
  // and the bottom data cells having bottom, left, and right padding. This creates individual looking cards, but
  // perfectly aligns their content.

  return (
    <div>
      {isShowOnlySubscriptions && (
        <div
          className={clsx('pt-6', {
            'relative before:content-[""] before:absolute before:top-0 before:left-0 before:h-full before:w-[3px] before:bg-purple-200':
              activeFormSection === ActiveFormSectionType.Subscriptions,
            'border-0':
              activeFormSection === ActiveFormSectionType.DisplayName ||
              activeFormSection === ActiveFormSectionType.OneTimeProducts ||
              !isShowOnlySubscriptions,
          })}
        >
          {validSubscriptions.length > 0 && (
            <ProductTitleHeader title="Plans" isPreview={isPreview} />
          )}

          {!initialSubscriptionProduct && isPreview ? (
            <ProductStoreItemSkeleton isOneTimeProduct={false} maxCard={4} />
          ) : (
            <table
              key="subscription"
              className={`border-spacing-y-0 border-spacing-x-[24px] table-fixed ${largeScreenSubscriptionTableWidth}`}
            >
              <tbody>
                {subscriptionColumns.map((column, columnIndex) => (
                  <>
                    <tr key={`${columnIndex}-subscription-header`}>
                      {column.map(({ product }, index) => (
                        <td
                          key={`${columnIndex}-subscription-header-${index}`}
                          className={`${TD_INNER_STYLES} w-[100%] rounded-t-lg border-t-[1px] border-l-[1px] border-r border-b-0 border-solid border-non-hover-border px-[24px]`}
                        >
                          <div className="pt-[24px] pb-[4px]">
                            <Heading
                              size="xl"
                              className="leading-5 break-words"
                            >
                              {product.name}
                            </Heading>
                          </div>
                        </td>
                      ))}
                    </tr>
                    {getDescription(column) && (
                      <tr key={`${columnIndex}-subscription-description`}>
                        {column.map(({ description }, index) => (
                          <td
                            key={`${columnIndex}-subscription-description-${index}`}
                            className={`${TD_INNER_STYLES} px-6`}
                          >
                            <RichTextStyles>
                              <div
                                dangerouslySetInnerHTML={{
                                  __html: description || '',
                                }}
                              />
                            </RichTextStyles>
                          </td>
                        ))}
                      </tr>
                    )}
                    <tr key={`${columnIndex}-subscription-amount`}>
                      {column.map(({ price }, index) => (
                        <td
                          key={`${columnIndex}-subscription-amount-${index}`}
                          className={`${TD_INNER_STYLES} px-6`}
                        >
                          <div className="py-3">
                            <Heading size="2xl" className="inline-block pr-1">
                              {formatCurrency(price.amount)}
                            </Heading>
                            <Body className="inline-block">
                              / {BillingPeriodMapper[price.interval ?? '']}
                            </Body>
                          </div>
                        </td>
                      ))}
                    </tr>
                    <tr key={`${columnIndex}-subscription-features-header`}>
                      {column.map(({ productId, priceId }, index) => (
                        <td
                          key={`${columnIndex}-subscription-features-header-${index}`}
                          className={`${TD_INNER_STYLES} px-[24px] w-[100%]`}
                        >
                          <div
                            className={clsx({
                              'pb-6': column.some(
                                (col) =>
                                  col.features && col.features.length > 0,
                              ),
                            })}
                          >
                            <Button
                              label="Subscribe"
                              variant="primary"
                              onClick={() => {
                                if (productId && priceId) {
                                  onClickPurchase(productId, priceId);
                                }
                              }}
                              className={clsx('w-full', {
                                'pointer-events-none': isPreview,
                              })}
                            />
                          </div>
                        </td>
                      ))}
                    </tr>
                    <tr key={`${columnIndex}-subscription-features`}>
                      {(() => {
                        const doesAnyColumnHaveFeatures = column.some(
                          ({ features }) => features && features.length > 0,
                        );

                        return column.map(({ features }, index) => (
                          <td
                            key={`${columnIndex}-subscription-features-${index}`}
                            className={`${TD_INNER_STYLES} w-[100%] p-0`}
                          >
                            <div
                              className={clsx(
                                'border-b-0 border-l-0 border-r-0 border-solid border-non-hover-border  mx-0',
                                {
                                  'border-t px-6 pt-6':
                                    doesAnyColumnHaveFeatures,
                                  'border-t-0': !doesAnyColumnHaveFeatures,
                                },
                              )}
                            >
                              {features && features.length > 0 && (
                                <ul className="space-y-2">
                                  {features.map((feature, i) => (
                                    <li
                                      key={`${columnIndex}-${index}-subscription-feature-${i}`}
                                      className="flex items-start justify-start gap-2"
                                    >
                                      <img
                                        src="/images/checkmark.svg"
                                        alt=""
                                        className="h-4 w-4 flex-shrink-0 py-1"
                                      />
                                      <Body className="leading-[22px]">
                                        <div
                                          style={{
                                            wordBreak: 'break-word',
                                          }}
                                        >
                                          {feature}
                                        </div>
                                      </Body>
                                    </li>
                                  ))}
                                </ul>
                              )}
                            </div>
                          </td>
                        ));
                      })()}
                    </tr>
                    <tr key={`${columnIndex}-subscription-features-bottom`}>
                      {column.map((index) => (
                        <td
                          key={`${columnIndex}-subscription-features-bottom-${index}`}
                          className={`${TD_INNER_STYLES} w-[100%] border-none p-0`}
                        >
                          <div className="rounded-b-lg border-b border-l border-r border-t-0 border-solid border-non-hover-border px-6 mx-0 pb-6 mb-6 " />
                        </td>
                      ))}
                    </tr>
                  </>
                ))}
              </tbody>
            </table>
          )}
        </div>
      )}
      {isShowOnlyOneTimeProducts && (
        <div
          className={clsx('pt-6', {
            'relative before:content-[""] before:absolute before:top-0 before:left-0 before:h-full before:w-[3px] before:bg-purple-200':
              activeFormSection === ActiveFormSectionType.OneTimeProducts,
            'border-0':
              activeFormSection === ActiveFormSectionType.DisplayName ||
              activeFormSection === ActiveFormSectionType.Subscriptions ||
              !isShowOnlyOneTimeProducts,
          })}
        >
          {oneTimeColumns.length > 0 && (
            <ProductTitleHeader
              title="One-time purchases"
              isPreview={isPreview}
            />
          )}
          {!initialOneTimeProduct && isPreview ? (
            <div className="pb-6">
              <ProductStoreItemSkeleton isOneTimeProduct={true} maxCard={4} />
            </div>
          ) : (
            <table
              key="one-time"
              className={`border-spacing-y-0 border-spacing-x-[24px] table-fixed  ${largeScreenOneTimeTableWidth}`}
            >
              <tbody>
                {oneTimeColumns.map((column, columnIndex) => (
                  <>
                    <tr key={`${columnIndex}-one-time-image`}>
                      {column.map(({ imageUrl, description }, index) => (
                        <td
                          key={`${columnIndex}-one-time-header-${index}`}
                          className={`${TD_INNER_STYLES} w-[100%] rounded-t-lg border-t-[1px] border-l-[1px] border-r border-b-0 border-solid border-non-hover-border p-0 overflow-hidden m-0`}
                        >
                          <div>
                            {imageUrl ? (
                              <div className="w-full h-[200px]">
                                <img
                                  src={imageUrl}
                                  alt={description}
                                  className="w-full h-[200px] object-cover"
                                />
                              </div>
                            ) : showPlaceholderImages ? (
                              <div className="w-full h-[200px] flex items-center justify-center bg-[#E8E8E8]">
                                <PlaceholderImage className="text-xl text-gray-small" />
                              </div>
                            ) : (
                              <div className="w-full h-[24px]" />
                            )}
                          </div>
                        </td>
                      ))}
                    </tr>
                    <tr key={`${columnIndex}-one-time-header`}>
                      {column.map(({ product }, index) => (
                        <td
                          key={`${columnIndex}-one-time-header-${index}`}
                          className={`${TD_INNER_STYLES} px-[24px]`}
                        >
                          <div
                            className={`${
                              showPlaceholderImages ? 'mt-[24px] ' : ''
                            }mb-[8px]`}
                          >
                            <Heading
                              size="xl"
                              className="leading-5 break-words"
                            >
                              {product.name}
                            </Heading>
                          </div>
                        </td>
                      ))}
                    </tr>

                    <tr key={`${columnIndex}-one-time-amount`}>
                      {column.map(({ price }, index) => (
                        <td
                          key={`${columnIndex}-one-time-amount-${index}`}
                          className={`${TD_INNER_STYLES} px-[24px]`}
                        >
                          <div className="mb-[12px]">
                            <Heading
                              size="2xl"
                              className="text-primary-500 leading-[32px]"
                            >
                              {formatCurrency(price.amount)}
                            </Heading>
                          </div>
                        </td>
                      ))}
                    </tr>
                    {getDescription(column) && (
                      <tr key={`${columnIndex}-one-time-description`}>
                        {column.map(({ description }, index) => (
                          <td
                            key={`${columnIndex}-one-time-description-${index}`}
                            className={`${TD_INNER_STYLES} px-[24px]`}
                          >
                            <div className="mb-[24px]">
                              <RichTextStyles>
                                <div
                                  style={{
                                    wordBreak: 'break-word',
                                  }}
                                  dangerouslySetInnerHTML={{
                                    __html: description || '',
                                  }}
                                />
                              </RichTextStyles>
                            </div>
                          </td>
                        ))}
                      </tr>
                    )}
                    <tr key={`${columnIndex}-one-time-purchase`}>
                      {column.map(({ productId, priceId }, index) => (
                        <td
                          key={`${columnIndex}-one-time-purchase-${index}`}
                          className={`${TD_INNER_STYLES} w-[100%] border-none p-0`}
                        >
                          <div className="rounded-b-lg border-b border-l border-r border-t-0 border-solid border-non-hover-border px-[24px] pb-[24px] mb-[24px] mx-0">
                            <Button
                              label="Purchase"
                              variant="primary"
                              onClick={() => {
                                if (productId && priceId) {
                                  onClickPurchase(productId, priceId);
                                }
                              }}
                              className={clsx('w-full', {
                                'pointer-events-none': isPreview,
                              })}
                            />
                          </div>
                        </td>
                      ))}
                    </tr>
                  </>
                ))}
              </tbody>
            </table>
          )}
        </div>
      )}
    </div>
  );
};
